import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'

interface NavigationProps {
  currentSection: string
  onSectionChange: (section: string) => void
}

const Navigation = ({ currentSection, onSectionChange }: NavigationProps) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [scrollSection, setScrollSection] = useState('home')

  const navItems = [
    { id: 'home', label: 'home' },
    { id: 'projects', label: 'projects' },
    { id: 'fun', label: 'about' },
    { id: 'contact', label: 'contact' }
  ]

  useEffect(() => {
    // Throttle function to limit how often the handler runs
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50)

          // Simple scroll-based section detection
          const sections = navItems.map(item => ({
            id: item.id,
            element: document.getElementById(item.id)
          })).filter(section => section.element)

          const scrollPosition = window.scrollY + window.innerHeight / 3

          for (let i = sections.length - 1; i >= 0; i--) {
            const section = sections[i]
            if (section.element) {
              const rect = section.element.getBoundingClientRect()
              const sectionTop = rect.top + window.scrollY

              if (scrollPosition >= sectionTop) {
                // Only update if section actually changes
                setScrollSection(prev => {
                  if (prev !== section.id) {
                    return section.id
                  }
                  return prev
                })
                break
              }
            }
          }
          ticking = false;
        })
        ticking = true;
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [navItems])

  const handleNavClick = (sectionId: string) => {
    console.log('Navigating to:', sectionId) // Debug log
    onSectionChange(sectionId)
    setScrollSection(sectionId) // Update scroll section immediately on click
  }

  // Use scrollSection for visual highlighting, but currentSection for actual navigation
  const activeSection = scrollSection

  return (
    <>
      {/* Desktop Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-effect py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="cursor-pointer flex items-center h-10 overflow-visible"
              onClick={() => handleNavClick('home')}
            >
              <img
                src="/Logo.png"
                alt="Claire Luo Logo"
                className="h-10 w-auto object-contain scale-[3.5]"
                style={{ maxWidth: '420px' }}
              />
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleNavClick(item.id)}
                  className={`relative px-4 py-2 text-md font-medium text-primary duration-300 ${
                    activeSection === item.id
                      ? 'text-primary-900'
                      : 'text-primary hover:text-primary'
                  }`}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-peachy-highlight-40 rounded-lg -z-10"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed top-20 left-0 right-0 z-40 glass-effect md:hidden"
          >
            <div className="container-max px-4 py-6">
              <div className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <motion.button
                    key={item.id}
                    whileHover={{ x: 10 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      handleNavClick(item.id)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`text-left px-4 py-3 text-lg font-medium transition-colors duration-300 rounded-lg ${
                      activeSection === item.id
                        ? 'text-primary-400 bg-peachy-highlight-40'
                        : 'text-primary hover:text-primary hover:bg-white/40'
                    }`}
                  >
                    {item.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navigation 